/**
 * API client per la gestione degli appuntamenti.
 *
 * Endpoints:
 * - GET /api/v1/appointments/ - Lista appuntamenti
 * - GET /api/v1/appointments/availability - Slot disponibili
 * - POST /api/v1/appointments/ - Crea appuntamento
 * - GET /api/v1/appointments/vets - Lista vet con specialità
 * - GET/POST/PUT /api/v1/admin/visit-types - Gestione tipi di visita
 */

import api from './auth';

export interface Appointment {
  id: string;
  patient_id: string;
  owner_id: string;
  vet_id: string;
  case_id: string | null;
  datetime_start: string;
  datetime_end: string;
  visit_type: string;
  gcal_event_id: string | null;
  status: string;
  notes: string | null;
  patient_name?: string;
  vet_name?: string;
}

export interface AppointmentDetail {
  id: string;
  patient: { id: string; name: string; species: string; breed?: string; owner_id: string };
  owner: { id: string; email: string; full_name?: string; phone?: string };
  vet: { id: string; full_name: string; email: string; specialties?: string[]; is_active: boolean } | null;
  datetime_start: string;
  datetime_end: string;
  visit_type: string;
  status: string;
  gcal_event_id: string | null;
  notes: string | null;
  case_id: string | null;
}

export interface TimeSlot {
  vet_id: string;
  vet_name: string;
  datetime: string;
}

export interface AvailabilityRequest {
  vet_id?: string;
  date: string;
  visit_type?: string;
}

export interface VisitType {
  key: string;
  label: string;
  duration_minutes: number;
  is_active: boolean;
}

export interface VisitTypeCreate {
  key: string;
  label: string;
  duration_minutes: number;
}

export interface VisitTypeUpdate {
  label?: string;
  duration_minutes?: number;
  is_active?: boolean;
}

export const appointmentsApi = {
  /**
   * Lista appuntamenti con filtri.
   */
  list: async (params?: { vet_id?: string; date_from?: string; date_to?: string; status?: string }): Promise<Appointment[]> => {
    const response = await api.get<Appointment[]>('/appointments/', { params });
    return response.data;
  },

  /**
   * Ottieni dettaglio appuntamento.
   */
  get: async (id: string): Promise<AppointmentDetail> => {
    const response = await api.get<AppointmentDetail>(`/appointments/${id}`);
    return response.data;
  },

  /**
   * Aggiorna appuntamento.
   */
  update: async (id: string, data: {
    datetime_start?: string;
    datetime_end?: string;
    visit_type?: string;
    notes?: string;
    status?: string;
  }): Promise<AppointmentDetail> => {
    const response = await api.put<AppointmentDetail>(`/appointments/${id}`, data);
    return response.data;
  },

  /**
   * Cancella appuntamento.
   */
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/appointments/${id}`);
    return response.data;
  },

  /**
   * Ottiene slot disponibili.
   */
  getAvailability: async (request: AvailabilityRequest): Promise<TimeSlot[]> => {
    const response = await api.get<TimeSlot[]>('/appointments/availability', { params: request });
    return response.data;
  },

  /**
   * Crea un appuntamento.
   */
  create: async (appointment: {
    patient_id: string;
    owner_id: string;
    vet_id: string;
    case_id?: string;
    datetime_start: string;
    visit_type: string;
  }): Promise<Appointment> => {
    const response = await api.post<Appointment>('/appointments/', appointment);
    return response.data;
  },

  /**
   * Lista vet attivi con specialità.
   */
  getVets: async (): Promise<{ id: string; full_name: string; specialties: string[] }[]> => {
    const response = await api.get('/appointments/vets');
    return response.data;
  },

  /**
   * Gestione tipi di visita.
   */
  visitTypes: {
    list: async (): Promise<VisitType[]> => {
      const response = await api.get<VisitType[]>('/admin/visit-types/');
      return response.data;
    },
    create: async (data: VisitTypeCreate): Promise<VisitType> => {
      const response = await api.post<VisitType>('/admin/visit-types/', data);
      return response.data;
    },
    update: async (key: string, data: VisitTypeUpdate): Promise<VisitType> => {
      const response = await api.put<VisitType>(`/admin/visit-types/${key}`, data);
      return response.data;
    },
    deactivate: async (key: string): Promise<void> => {
      await api.delete(`/admin/visit-types/${key}`);
    },
    activate: async (key: string): Promise<void> => {
      await api.post(`/admin/visit-types/${key}/activate`);
    },
  },
};
